/**
 * UserContext - Global user state management
 * Manages user authentication state, profile data, and preferences
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
  useMemo
} from 'react';
import { TokenService } from '../services/TokenService';
import { UserProfile, CreditLimit } from '../types/user.types';

// ============================================================================
// TYPES
// ============================================================================

export interface UserState {
  isAuthenticated: boolean;
  isLoading: boolean;
  customerId: string | null;
  profile: UserProfile | null;
  creditLimit: CreditLimit | null;
  token: { access_token: string } | null;
  error: string | null;
}

export interface UserContextType {
  state: UserState;
  setAuthenticated: (authenticated: boolean) => void;
  setProfile: (profile: UserProfile | null) => void;
  setCreditLimit: (creditLimit: CreditLimit | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  refreshToken: () => void;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const tokenService = new TokenService();

const getInitialState = (): UserState => {
  const rawToken = tokenService.getToken();
  const customerId = tokenService.getCustomerId();
  const token = rawToken?.access_token ? { access_token: rawToken.access_token } : null;
  
  return {
    isAuthenticated: !!rawToken,
    isLoading: false,
    customerId,
    profile: null,
    creditLimit: null,
    token,
    error: null
  };
};

// ============================================================================
// CONTEXT
// ============================================================================

const UserContext = createContext<UserContextType | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps): React.JSX.Element {
  const [state, setState] = useState<UserState>(getInitialState);

  // Listen for token changes
  useEffect(() => {
    const checkAuth = () => {
      const rawToken = tokenService.getToken();
      const customerId = tokenService.getCustomerId();
      const token = rawToken?.access_token ? { access_token: rawToken.access_token } : null;
      
      setState(prev => ({
        ...prev,
        isAuthenticated: !!rawToken,
        customerId,
        token
      }));
    };

    // Check on mount
    checkAuth();

    // Set up interval to check token validity
    const interval = setInterval(checkAuth, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const setAuthenticated = useCallback((authenticated: boolean) => {
    setState(prev => ({ ...prev, isAuthenticated: authenticated }));
  }, []);

  const setProfile = useCallback((profile: UserProfile | null) => {
    setState(prev => ({ ...prev, profile }));
  }, []);

  const setCreditLimit = useCallback((creditLimit: CreditLimit | null) => {
    setState(prev => ({ ...prev, creditLimit }));
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const logout = useCallback(() => {
    tokenService.removeOnlyToken();
    setState({
      isAuthenticated: false,
      isLoading: false,
      customerId: null,
      profile: null,
      creditLimit: null,
      token: null,
      error: null
    });
  }, []);

  const refreshToken = useCallback(() => {
    const rawToken = tokenService.getToken();
    const customerId = tokenService.getCustomerId();
    const token = rawToken?.access_token ? { access_token: rawToken.access_token } : null;
    
    setState(prev => ({
      ...prev,
      token,
      customerId,
      isAuthenticated: !!rawToken
    }));
  }, []);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value = useMemo<UserContextType>(() => ({
    state,
    setAuthenticated,
    setProfile,
    setCreditLimit,
    setLoading,
    setError,
    logout,
    refreshToken
  }), [
    state,
    setAuthenticated,
    setProfile,
    setCreditLimit,
    setLoading,
    setError,
    logout,
    refreshToken
  ]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useUser(): UserContextType {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
